"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.waitForCondition = void 0;
const asAbortable_1 = require("./asAbortable");
const wait_1 = require("./wait");
const lodash_1 = require("lodash");
const composeAbortSignals_1 = require("./composeAbortSignals");
function throwError(userAborted, conditionFn, onTimeoutContext) {
    const action = userAborted ? 'aborted' : 'timed out';
    let msg = `waitForCondition: ${action} before "${conditionFn.toString()}" became true`;
    if (onTimeoutContext) {
        msg += `\n${onTimeoutContext()}`;
    }
    throw new Error(msg);
}
/**
 * Wait for a condition to become true by re-evaluating `conditionFn` every `retryInterval` milliseconds.
 *
 * @param conditionFn condition to be evaluated; should return boolean or Promise<boolean> and have
 * no side effects.
 * @param timeout amount of time in milliseconds to wait for
 * @param retryInterval how often, in milliseconds, to re-evaluate condition
 * @param abortSignal pass an abort signal to cancel prematurely
 * @param onTimeoutContext evaluated only on timeout. Used to associate human-friendly textual context to error.
 * @returns {Promise<void>} resolves immediately if
 * conditionFn evaluates to true on a retry attempt within timeout. If timeout
 * is reached with conditionFn never evaluating to true, rejects.
 */
const waitForCondition = async (conditionFn, timeout = 5000, retryInterval = 100, abortSignal, onTimeoutContext) => {
    let userAborted = abortSignal?.aborted ?? false;
    if (userAborted) {
        throwError(userAborted, conditionFn, onTimeoutContext);
    }
    abortSignal?.addEventListener('abort', () => { userAborted = true; }, { once: true });
    const timeoutAbortSignal = AbortSignal.timeout(timeout); // TODO: remove `as any` cast when @types/node has support...
    const composedSignal = (0, composeAbortSignals_1.composeAbortSignals)(...(0, lodash_1.compact)([timeoutAbortSignal, abortSignal]));
    try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const result = await (0, asAbortable_1.asAbortable)(Promise.resolve(conditionFn()), composedSignal);
            if (result) {
                return;
            }
            await (0, wait_1.wait)(retryInterval, composedSignal);
        }
    }
    catch (e) {
        if (e.code === 'AbortError') {
            throwError(userAborted, conditionFn, onTimeoutContext);
        }
        throw e;
    }
};
exports.waitForCondition = waitForCondition;
//# sourceMappingURL=waitForCondition.js.map